import React, { useEffect } from "react";
import { X, ZoomIn, ZoomOut } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  alt?: string;
}

export const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  alt,
}) => {
  const { t } = useTranslation("common");
  const [zoom, setZoom] = React.useState(1);

  // Reset zoom when modal opens
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
 className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
      onClick={handleBackdropClick}
    >
      {/* Controls */}
 <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
 className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t("zoomOut")}
        >
 <ZoomOut className="w-5 h-5" />
        </button>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 3}
 className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title={t("zoomIn")}
        >
 <ZoomIn className="w-5 h-5" />
        </button>
        <button
          onClick={onClose}
 className="p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-colors"
          title={t("closeEsc")}
        >
 <X className="w-5 h-5" />
        </button>
      </div>

      {/* Zoom indicator */}
      {zoom !== 1 && (
 <div className="absolute top-4 left-4 z-10">
 <div className="px-3 py-1 bg-white bg-opacity-20 text-white rounded-full text-sm">
            {Math.round(zoom * 100)}%
          </div>
        </div>
      )}

      {/* Image container */}
 <div className="relative w-full h-full flex items-center justify-center p-8 overflow-auto">
        <img
          src={imageUrl}
          alt={alt ?? t("image")}
 className="max-w-none object-contain transition-transform duration-200"
          style={{
            transform: `scale(${zoom})`,
            cursor: zoom > 1 ? "grab" : "default",
          }}
          draggable={false}
        />
      </div>

      {/* Instructions */}
 <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
 <div className="px-4 py-2 bg-white bg-opacity-20 text-white rounded-full text-sm">
          {t("clickOutsideToClose")}
        </div>
      </div>
    </div>
  );
};
